import axiosClient from "../api/axiosClient";

/**
 * @Room service — access level, pending join requests, and social links.
 * Ported 1:1 from the React Native app's setting-a-room, a-room-social-link,
 * and pending-approval screens (src/Screens/*) and the API functions/
 * constants they call. No new backend endpoints — all of the below are
 * already live. Room title editing and the QR/share data reuse existing
 * functions (createEventService.updateEvent, chatService.getMyChatRooms) —
 * not duplicated here.
 */

// --- Access level (Public/Private) ---
export const getRoomAccess = (eventId) => axiosClient.get(`/Events/${eventId}/gallery-access`);

/** @param {string|number} eventId @param {1|2} accessMode - 1 = Public, 2 = Private */
export const updateRoomAccess = (eventId, accessMode) =>
  axiosClient.post(`/Events/${eventId}/gallery-access`, { accessMode, flynnPassEnabled: false });

// --- Deactivate/Reactivate (owner-only) ---
// An @Room must never be permanently deleted from the Panel — only
// deactivated/reactivated, per the existing backend feature
// (AroomReactivationService / Event.IsClosed). This is distinct from
// Public/Private access above.
export const deactivateRoom = (eventId) => axiosClient.post(`/Events/${eventId}/deactivate`);
export const reactivateRoom = (eventId) => axiosClient.post(`/Events/${eventId}/reactivate`);

// --- Pending room-join requests (distinct from Phase 5's host-access requests) ---
export const getPendingRoomRequests = (eventId) =>
  axiosClient.get(`/Events/${eventId}/verification-requests`);

/**
 * @param {string|number} requestId
 * @param {"approve"|"reject"} approve
 * @param {{requireAdditionalInfo?: boolean, message?: string}} [opts] - only used when rejecting
 */
export const respondToRoomRequest = (requestId, approve, opts = {}) =>
  axiosClient.post(
    `/Events/verification/${requestId}/${approve}`,
    approve === "reject" ? { requireAdditionalInfo: !!opts.requireAdditionalInfo, message: opts.message || "" } : {}
  );

// --- Social links (account-level) ---
export const getSocialLinks = () => axiosClient.get("/PromoterPlaceholder/social-links");

export const getSocialPlatforms = () => axiosClient.get("/PromoterPlaceholder/GetSocialPlatforms");

/** @param {{socialPlatformId, url}[]} links */
export const saveSocialLinks = (links) =>
  axiosClient.post("/PromoterPlaceholder/social-links", JSON.stringify({ links }));

export const deactivateSocialLink = (platformId) =>
  axiosClient.patch(`/PromoterPlaceholder/social-links/${platformId}/toggle`);

// --- Greeting message (account-level "PromoterPlaceholder", Type=1/Text) ---
// Ported from mobile's onGetPromoterPlaceholder/CreatePromoterPlaceholder —
// this is account-wide (PromoterPlaceholder has no EventId/RoomId field at
// all), not scoped to the @Room it's opened from, same as the watermark and
// social links above.
export const getPromoterPlaceholders = () => axiosClient.get("/PromoterPlaceholder");

export const createPromoterPlaceholder = ({ title, content }) =>
  axiosClient.post("/PromoterPlaceholder", { Title: title, Content: content, Type: 1 });

export const updatePromoterPlaceholder = (id, { title, content }) =>
  axiosClient.put(`/PromoterPlaceholder/${id}`, { Title: title, Content: content, Type: 1 });

export const deletePromoterPlaceholder = (id) => axiosClient.delete(`/PromoterPlaceholder/${id}`);
